import { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import { getDisplaysIdsFromMap } from "../../../../services/display";


// Number of display IDs fetched per chunk when infinite-scrolling the screen list.
const SCREENS_CHUNK_SIZE = 15;

export function useDisplayMapList({
  displaysMap,
  displaysMapMode,
  displays,
  getDisplays,
  date_from,
  date_to,
  client,
  dsp,
  currency,
  programmatic,
  editCampaignActive,
  campaignCurrency,
  campaignInvoiceIssuingCountry,
  invoiceIssuingCountry,
  isGuaranteed,
}) {
  const [state, setState] = useState({
    displaysIds: [],
    loadedDisplays: [],
    loadedCount: 0,
    loadingMore: false,
    pendingCount: 0,
  });

  const listContentRef = useRef(null);

  const stateRef = useRef(state);
  stateRef.current = state;

  const propsRef = useRef();
  propsRef.current = {
    date_from,
    date_to,
    client,
    dsp,
    currency,
    programmatic,
    editCampaignActive,
    campaignCurrency,
    campaignInvoiceIssuingCountry,
    invoiceIssuingCountry,
    isGuaranteed,
  };

  const loadNextChunk = useCallback(() => {
    const { displaysIds, loadedCount, loadingMore } = stateRef.current;
    if (loadingMore) return;
    if (loadedCount >= displaysIds.length) return;

    const chunk = displaysIds.slice(
      loadedCount,
      loadedCount + SCREENS_CHUNK_SIZE,
    );
    if (chunk.length === 0) return;

    const p = propsRef.current;
    setState((prev) => ({
      ...prev,
      loadingMore: true,
      pendingCount: chunk.length,
    }));

    getDisplays({
      list_id: chunk,
      date_from: moment(p.date_from || new Date()).format("YYYY-MM-DD"),
      date_to: moment(p.date_to || new Date()).format("YYYY-MM-DD"),
      currency: p.editCampaignActive ? p.campaignCurrency : p.currency,
      invoice_issuing_country: p.editCampaignActive
        ? p.campaignInvoiceIssuingCountry
        : p.invoiceIssuingCountry,
      client_id: p.client ? p.client.id : null,
      external_dsp_id: p.dsp ? p.dsp.id : null,
      smart_campaign_enabled: p.programmatic === "programmatic" ? 1 : null,
      is_campaign: p.editCampaignActive ? 1 : 0,
      is_guaranteed: p.isGuaranteed ? 1 : 0,
    });
  }, [getDisplays]);

  // If the list doesn't yet overflow its container, the user can't trigger
  // a scroll. Auto-fetch the next chunk until there's something to scroll.
  const maybeLoadMoreUntilScrollable = useCallback(() => {
    const el = listContentRef.current;
    if (!el) return;
    const { loadedCount, displaysIds } = stateRef.current;
    if (loadedCount >= displaysIds.length) return;
    if (el.scrollHeight <= el.clientHeight + 10) {
      loadNextChunk();
    }
  }, [loadNextChunk]);

  const handleListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (!el) return;
      const threshold = 40;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        loadNextChunk();
      }
    },
    [loadNextChunk],
  );

  // When the map fetch completes, derive the display ids from the
  // cluster/detail response and (re)start the infinite-scroll list.
  // The redux slice produces a new array reference on every successful fetch,
  // so a reference check is enough — no deep compare needed.
  const prevDisplaysMapRef = useRef();
  useEffect(() => {
    if (prevDisplaysMapRef.current !== displaysMap) {
      prevDisplaysMapRef.current = displaysMap;
      const ids = getDisplaysIdsFromMap(displaysMap, displaysMapMode);
      setState({
        displaysIds: ids,
        loadedDisplays: [],
        loadedCount: 0,
        loadingMore: false,
        pendingCount: 0,
      });
    }
  }, [displaysMap, displaysMapMode]);

  // After resetting from a new displaysMap, kick off the first chunk fetch.
  useEffect(() => {
    if (
      state.displaysIds.length > 0 &&
      state.loadedCount === 0 &&
      !state.loadingMore
    ) {
      loadNextChunk();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.displaysIds]);

  // Append the just-fetched chunk to the accumulated list and unlock loading.
  const prevDisplaysRef = useRef(displays);
  useEffect(() => {
    if (
      prevDisplaysRef.current !== displays &&
      stateRef.current.loadingMore
    ) {
      const fetched = displays || [];
      setState((prev) => ({
        ...prev,
        loadedDisplays: [...prev.loadedDisplays, ...fetched],
        loadedCount: prev.loadedCount + prev.pendingCount,
        loadingMore: false,
        pendingCount: 0,
      }));
    }
    prevDisplaysRef.current = displays;
  }, [displays]);

  // After appending, if the list still doesn't overflow the container, keep
  // loading so the user can actually reach the scroll boundary to trigger more.
  useEffect(() => {
    if (
      !state.loadingMore &&
      state.loadedDisplays.length > 0 &&
      state.loadedCount < state.displaysIds.length
    ) {
      maybeLoadMoreUntilScrollable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loadedDisplays.length, state.loadingMore]);

  return {
    listContentRef,
    loadedDisplays: state.loadedDisplays,
    loadingMore: state.loadingMore,
    displaysIds: state.displaysIds,
    handleListScroll,
  };
}

export default useDisplayMapList;
